/**
 * ADD NOTE DIALOG — popup per aggiungere una nota al campo `notes` del
 * preventivo. Al salvataggio la nota viene concatenata in fondo al testo
 * esistente, preservando una newline separatrice: `notes` può essere null, e in
 * quel caso la nota diventa il contenuto iniziale.
 *
 * Stile allineato agli altri dialog del CRM (`.app-dialog__header/__body/__footer`).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useAppDispatch } from '../../store/hooks';
import { useToast } from '../../components/useToast';
import { updateQuotation } from './quotationThunks';
import { appendNote } from './notesUtils';
import type { Quotation } from './quotationMappers';

export function AddNoteDialog({
  quotation,
  onClose,
}: {
  quotation: Quotation;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    const trimmed = note.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      await dispatch(updateQuotation({
        quotationId: quotation.quotationId,
        notes: appendNote(quotation.notes, trimmed),
      })).unwrap();
      toast({ severity: 'success', message: t('quotation.addNoteDialog.toastSuccess') });
      onClose();
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">{t('quotation.addNoteDialog.title')}</span>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <TextField
          label={t('quotation.addNoteDialog.label')}
          placeholder={t('quotation.addNoteDialog.placeholder')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          fullWidth
          multiline
          rows={6}
          autoFocus
          disabled={saving}
          className="form-field"
        />
      </DialogContent>
      <DialogActions className="app-dialog__footer">
        <Button onClick={handleSave} variant="contained" disabled={!note.trim() || saving}>
          {t('common.save')}
        </Button>
        <Button onClick={onClose} variant="outlined">
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
